import { ChangeEvent, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import Product from '@/models/product';
import { useProducts } from '@/services/products';
import ProductModal from './components/product-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';

const valueFor = (row: Product, key: string): string | number => {
    switch (key) {
        case 'price':
            return row.price?.unitAmount ?? -1;
        case 'status':
            return row.comingSoon ? 'coming' : row.active ? 'active' : 'hidden';
        default: {
            const v = (row as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    return (a: Product, b: Product) => {
        const av = valueFor(a, orderBy);
        const bv = valueFor(b, orderBy);
        if (av < bv) return order === 'desc' ? 1 : -1;
        if (av > bv) return order === 'desc' ? -1 : 1;
        return 0;
    };
};

const statusLabel = (p: Product): string => (p.comingSoon ? 'Coming soon' : p.active ? 'Active' : 'Hidden');
const priceLabel = (p: Product): string => p.price?.formatted ?? (p.comingSoon ? '—' : 'No price set');

const Products = () => {
    const { data: products, isLoading } = useProducts();
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('orderIndex');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtered, setFiltered] = useState<Product[]>(products?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string>();

    useEffect(() => {
        if (products?.data) {
            setFiltered(products.data);
        }
    }, [products]);

    // Clamp so a mutation/search that shrinks the list never strands the user on
    // an out-of-range (empty) page — without surprising them with a reset on a
    // benign background refetch.
    const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));

    const toggle = () => setIsOpen((prev) => !prev);

    const handleRequestAdd = () => {
        setSelectedProductId(undefined);
        toggle();
    };

    const handleRequestSort = (_: unknown, property: string) => {
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }
        setOrder(orderTemp);
        setOrderBy(property);
    };

    const handleClick = (id: string) => {
        setSelectedProductId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Products</h3>
                    <h3 className="page-subhead subhead">
                        The accessories catalog. Pricing comes live from Stripe — map each product to its Stripe price below.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Name or Slug"
                                            type="text"
                                            onChange={(e) => {
                                                const q = e.target.value.toLowerCase();
                                                setFiltered(
                                                    products?.data?.filter(
                                                        (p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
                                                    ) || [],
                                                );
                                            }}
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filtered
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
                                                    .map((d) => (
                                                        <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {d.name}
                                                            </TableCell>
                                                            <TableCell>{d.slug}</TableCell>
                                                            <TableCell>{priceLabel(d)}</TableCell>
                                                            <TableCell>{statusLabel(d)}</TableCell>
                                                            <TableCell>{d.orderIndex}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filtered.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center">
                                                            No products yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filtered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={safePage}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ProductModal isOpen={isOpen} toggle={toggle} selectedProductId={selectedProductId} />
        </Container>
    );
};

export default Products;
